import { Fragment, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, CalendarClock, ChevronRight, History, Info, Pencil, Play, RefreshCw, StopCircle, Zap,
} from 'lucide-react';
import {
  aggiornamentiService,
  type Aggiornamento,
  type AvviatoDa,
  type ElencoAggiornamenti,
  type Esecuzione,
  type Esito,
  type Frequenza,
  type StatoAggiornamento,
} from '../../services/aggiornamentiService';
import { messaggioDiretto } from '../../components/admin/AggiornaDalGestionale';
import CodiciArticoloCombobox from '../../components/admin/CodiciArticoloCombobox';
import ContoCombobox from '../../components/admin/ContoCombobox';

/**
 * "Aggiornamenti dal gestionale" (`.scratch/aggiornamenti-erp/spec.md`, variante C
 * del prototipo sul branch `prototype/aggiornamenti-admin`): elenco dei dati a
 * sinistra, a destra il dato scelto con il suo Aggiornamento pianificato e il
 * suo Aggiornamento diretto affiancati, poi lo storico delle esecuzioni.
 */

const STATO: Record<StatoAggiornamento, { label: string; badge: string; dot: string }> = {
  inattivo: { label: 'Pianificato', badge: 'text-gray-700 bg-gray-50 border-gray-200', dot: 'bg-gray-300' },
  in_corso: { label: 'In esecuzione', badge: 'text-blue-700 bg-blue-50 border-blue-200', dot: 'bg-blue-500 animate-pulse' },
  in_coda: {
    label: 'Aggiornamento previsto al termine della lettura completa',
    badge: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500',
  },
  in_attesa_turno: {
    label: 'In coda: limite di 2 esecuzioni contemporanee',
    badge: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500',
  },
  disabilitato: { label: 'Disattivato', badge: 'text-gray-500 bg-white border-gray-200', dot: 'bg-gray-300' },
};

const ESITO: Record<Esito, { label: string; badge: string; dot: string }> = {
  ok: { label: 'Completata', badge: 'text-green-700 bg-green-50 border-green-200', dot: 'bg-green-500' },
  partial: { label: 'Completata con errori', badge: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  error: { label: 'Errore', badge: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
  stopped: { label: 'Interrotta', badge: 'text-gray-700 bg-gray-50 border-gray-200', dot: 'bg-gray-400' },
  interrupted: { label: 'Interrotta dal riavvio', badge: 'text-gray-700 bg-gray-50 border-gray-200', dot: 'bg-gray-400' },
  running: { label: 'In esecuzione', badge: 'text-blue-700 bg-blue-50 border-blue-200', dot: 'bg-blue-500' },
  invariato: { label: 'Nessuna modifica', badge: 'text-gray-700 bg-gray-50 border-gray-200', dot: 'bg-gray-400' },
};

const GIORNI = ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica'];
const GIORNI_BREVI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const TH = 'text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap';

function testoOrario(f: { ora: string; giorni?: number[] }): string {
  if (!f.giorni?.length) return `ogni giorno alle ${f.ora}`;
  const nomi = f.giorni.map((g) => GIORNI[g]);
  const elenco = nomi.length > 1 ? `${nomi.slice(0, -1).join(', ')} e ${nomi[nomi.length - 1]}` : nomi[0];
  return `${elenco} alle ${f.ora}`;
}

function testoFrequenza(a: Aggiornamento): string {
  const f = a.frequenza;
  let base: string;
  if (f.tipo === 'alle') base = testoOrario(f);
  else if (f.minuti % 1440 === 0) base = f.minuti === 1440 ? 'ogni giorno' : `ogni ${f.minuti / 1440} giorni`;
  else if (f.minuti % 60 === 0) base = f.minuti === 60 ? 'ogni ora' : `ogni ${f.minuti / 60} ore`;
  else base = `ogni ${f.minuti} minuti`;
  return a.completa ? `${base} · lettura completa ${testoOrario(a.completa).replace('ogni giorno ', '')}` : base;
}

function quando(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const oggi = new Date();
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });
  if (d.toDateString() === oggi.toDateString()) return ora;
  const domani = new Date(oggi.getTime() + 86_400_000);
  if (d.toDateString() === domani.toDateString()) return `domani ${ora}`;
  return `${d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Rome' })} ${ora}`;
}

function testoAvvio(a: AvviatoDa): string {
  switch (a.tipo) {
    case 'manuale': return a.utente ? `Avvio manuale — ${a.utente}` : 'Avvio manuale';
    case 'scrittura': return a.dettaglio ? `Rilettura dopo scrittura — ${a.dettaglio}` : 'Rilettura dopo scrittura';
    case 'apertura': return a.dettaglio ? `Rilettura all'apertura — ${a.dettaglio}` : "Rilettura all'apertura";
    case 'diretto': return a.dettaglio ? `Aggiornamento diretto — ${a.dettaglio}` : 'Aggiornamento diretto';
    case 'webhook': return 'Webhook del gestionale';
    default: return 'Pianificazione';
  }
}

function Badge({ cls, dot, children }: { cls: string; dot?: string; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}{children}
    </span>
  );
}

function EsitoBadge({ esito }: { esito: Esito }) {
  const e = ESITO[esito] ?? ESITO.error;
  return <Badge cls={e.badge} dot={e.dot}>{e.label}</Badge>;
}

function Card({ icon: Icon, title, right, children }: {
  icon: typeof Zap; title: string; right?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden flex flex-col">
      <div className="px-5 py-4 border-b flex items-center gap-3">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <h2 className="font-semibold">{title}</h2>
        {right && <div className="ml-auto">{right}</div>}
      </div>
      <div className="p-5 flex-1 flex flex-col">{children}</div>
    </div>
  );
}

function Nota({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-auto -mx-5 -mb-5 px-5 py-3 border-t bg-gray-50 flex items-start gap-2 text-xs text-muted-foreground">
      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

/** Modifica della frequenza: "ogni N minuti" o "alle HH:MM", eventualmente in alcuni giorni. */
function FrequenzaEditor({ valore, onAnnulla, onSalva }: {
  valore: Frequenza; onAnnulla: () => void; onSalva: (f: Frequenza) => Promise<void>;
}) {
  const [tipo, setTipo] = useState<Frequenza['tipo']>(valore.tipo);
  const [minuti, setMinuti] = useState(valore.tipo === 'ogni' ? valore.minuti : 60);
  const [ora, setOra] = useState(valore.tipo === 'alle' ? valore.ora : '02:00');
  const [giorni, setGiorni] = useState<number[]>(valore.tipo === 'alle' ? valore.giorni ?? [] : []);
  const [salvando, setSalvando] = useState(false);
  const input = 'rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B5EA7]/30';

  const salva = async () => {
    setSalvando(true);
    try {
      await onSalva(tipo === 'ogni' ? { tipo, minuti } : { tipo, ora, ...(giorni.length ? { giorni } : {}) });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border bg-gray-50 p-3">
      <label className="flex items-center gap-2 text-sm">
        <input type="radio" className="accent-[#2B5EA7]" checked={tipo === 'ogni'} onChange={() => setTipo('ogni')} />
        Ogni
        <input type="number" min={1} max={10080} value={minuti} disabled={tipo !== 'ogni'}
          onChange={(e) => setMinuti(Number(e.target.value))} className={`w-24 ${input}`} />
        minuti
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="radio" className="accent-[#2B5EA7]" checked={tipo === 'alle'} onChange={() => setTipo('alle')} />
        Alle
        <input type="time" value={ora} disabled={tipo !== 'alle'} onChange={(e) => setOra(e.target.value)}
          className={`w-28 ${input}`} />
      </label>
      {tipo === 'alle' && (
        <div className="space-y-1.5">
          <div className="text-xs text-muted-foreground">Solo in questi giorni (nessuno = tutti i giorni)</div>
          <div className="flex flex-wrap gap-1.5">
            {GIORNI_BREVI.map((g, i) => (
              <button key={g} type="button"
                onClick={() => setGiorni((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort()))}
                className={`h-7 px-3 rounded-full text-xs font-medium border ${giorni.includes(i) ? 'bg-[#2B5EA7] text-white border-[#2B5EA7]' : 'bg-white'}`}>
                {g}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onAnnulla} className="h-9 px-4 rounded-lg border text-sm bg-white hover:bg-gray-50">Annulla</button>
        <button onClick={salva} disabled={salvando}
          className="h-9 px-5 rounded-lg bg-[#2B5EA7] text-white text-sm font-medium hover:bg-[#1E4D8C] disabled:opacity-50">
          Salva
        </button>
      </div>
    </div>
  );
}

const DIRETTI: Record<string, { titolo: string }> = {
  ordine: { titolo: 'Aggiornamento diretto per numero ordine' },
  articolo: { titolo: 'Aggiornamento diretto per codice articolo' },
  conto: { titolo: 'Aggiornamento diretto per conto' },
};

/** Aggiornamento diretto di un conto: tutto ciò che riguarda quel Cliente. */
function DirettoConto({ onFatto }: { onFatto: () => void }) {
  const [conto, setConto] = useState('');
  const numero = /^\d+$/.test(conto.trim()) ? Number(conto.trim()) : null;
  const [inCorso, setInCorso] = useState(false);
  const aggiorna = async () => {
    if (numero === null) return;
    setInCorso(true);
    try {
      const r = await aggiornamentiService.direttoConto(numero);
      const m = messaggioDiretto(r, `Conto ${numero}`);
      toast[m.tipo](m.tipo === 'success'
        ? `${m.testo}: ${r.ordini ?? 0} ordini letti.${r.documenti ? " I Documenti dell'anno si caricano in background." : ''}`
        : m.testo, { duration: 6000 });
      if (m.tipo === 'success') setConto('');
      onFatto();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Il gestionale non ha risposto: riprova fra qualche istante.');
    } finally {
      setInCorso(false);
    }
  };
  return (
    <>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Rilegge anagrafica e destinazioni, Listino assegnato e condizione di pagamento, righe di listino proprie
        e tutti gli ordini del conto, poi avvia il caricamento dei Documenti dell&apos;anno corrente.
      </p>
      <div className="space-y-1.5">
        <label htmlFor="diretto-conto" className="text-xs font-medium text-muted-foreground">Conto</label>
        <ContoCombobox valore={conto} onChange={setConto} onInvio={aggiorna} />
        <p className="text-[11px] text-muted-foreground">Il codice conto del Cliente sul gestionale.</p>
      </div>
      <div className="flex justify-end">
        <button onClick={aggiorna} disabled={inCorso || numero === null} data-testid="diretto-conto-aggiorna"
          className="h-9 px-5 rounded-lg bg-[#2B5EA7] text-white text-sm font-medium hover:bg-[#1E4D8C] transition inline-flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${inCorso ? 'animate-spin' : ''}`} />Aggiorna dal gestionale
        </button>
      </div>
    </>
  );
}

/** Aggiornamento diretto di uno o più articoli per codice. */
function DirettoArticoli({ onFatto }: { onFatto: () => void }) {
  const [codici, setCodici] = useState<string[]>([]);
  const [inCorso, setInCorso] = useState(false);
  const aggiorna = async () => {
    if (!codici.length) return;
    setInCorso(true);
    try {
      const r = await aggiornamentiService.direttoArticoli(codici);
      const cosa = codici.length === 1 ? `Articolo ${codici[0]}` : `${codici.length} articoli`;
      const m = messaggioDiretto(r, cosa);
      toast[m.tipo](m.tipo === 'success'
        ? `${m.testo}${r.mancanti?.length ? ` (non presenti sul gestionale: ${r.mancanti.join(', ')})` : ''}. Testi web, Codici OEM, kit e giacenze si aggiornano in background.`
        : m.testo, { duration: 6000 });
      if (m.tipo === 'success') setCodici([]);
      onFatto();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Il gestionale non ha risposto: riprova fra qualche istante.');
    } finally {
      setInCorso(false);
    }
  };
  return (
    <>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Rilegge subito anagrafica, righe di listino e sconti degli articoli indicati (~7 s), poi avvia Testi web,
        Codici OEM, Kit e Giacenze (~70 s).
      </p>
      <div className="space-y-1.5">
        <label htmlFor="diretto-codici-articolo" className="text-xs font-medium text-muted-foreground">Codici articolo</label>
        <CodiciArticoloCombobox scelti={codici} onChange={setCodici} />
        <p className="text-[11px] text-muted-foreground">È possibile indicare più articoli: premi Invio o scegli dall&apos;elenco dopo ciascuno.</p>
      </div>
      <div className="flex justify-end">
        <button onClick={aggiorna} disabled={inCorso || !codici.length} data-testid="diretto-articoli-aggiorna"
          className="h-9 px-5 rounded-lg bg-[#2B5EA7] text-white text-sm font-medium hover:bg-[#1E4D8C] transition inline-flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${inCorso ? 'animate-spin' : ''}`} />Aggiorna dal gestionale
        </button>
      </div>
    </>
  );
}

/** Aggiornamento diretto di un ordine per Numero ordine. */
function DirettoOrdine({ onFatto }: { onFatto: () => void }) {
  const [numero, setNumero] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const aggiorna = async () => {
    if (!numero.trim()) return;
    setInCorso(true);
    try {
      const m = messaggioDiretto(await aggiornamentiService.direttoOrdine(numero.trim()), `Ordine ${numero.trim()}`);
      toast[m.tipo](m.testo);
      if (m.tipo === 'success') setNumero('');
      onFatto();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Il gestionale non ha risposto: riprova fra qualche istante.');
    } finally {
      setInCorso(false);
    }
  };
  return (
    <>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Rilegge testata, stato e righe dell&apos;ordine e aggiorna l&apos;Ordine web collegato, con le stesse email di stato.
      </p>
      <div className="space-y-1.5">
        <label htmlFor="diretto-numero-ordine" className="text-xs font-medium text-muted-foreground">Numero ordine</label>
        <input id="diretto-numero-ordine" value={numero} onChange={(e) => setNumero(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') aggiorna(); }} placeholder="Es. OW/2026/00048"
          className="w-full h-10 rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B5EA7]/30"
          data-testid="diretto-numero-ordine" />
        <p className="text-[11px] text-muted-foreground">Il numero come lo vede il Cliente: serie, anno e progressivo.</p>
      </div>
      <div className="flex justify-end">
        <button onClick={aggiorna} disabled={inCorso || !numero.trim()} data-testid="diretto-ordine-aggiorna"
          className="h-9 px-5 rounded-lg bg-[#2B5EA7] text-white text-sm font-medium hover:bg-[#1E4D8C] transition inline-flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${inCorso ? 'animate-spin' : ''}`} />Aggiorna dal gestionale
        </button>
      </div>
    </>
  );
}

export default function AdminAggiornamentiPage() {
  const [params, setParams] = useSearchParams();
  const [dati, setDati] = useState<ElencoAggiornamenti | null>(null);
  const [storico, setStorico] = useState<Esecuzione[]>([]);
  const [modificaFrequenza, setModificaFrequenza] = useState(false);
  const [azione, setAzione] = useState(false);
  const [limite, setLimite] = useState(20);

  const aggiornamenti = dati?.aggiornamenti ?? [];
  const selId = params.get('dato') ?? aggiornamenti[0]?.id;
  const x = aggiornamenti.find((a) => a.id === selId) ?? aggiornamenti[0];

  const carica = useCallback(async () => {
    try {
      setDati(await aggiornamentiService.elenco());
    } catch {
      toast.error('Impossibile leggere gli aggiornamenti');
    }
  }, []);

  const caricaStorico = useCallback(async (id: string) => {
    try {
      setStorico(await aggiornamentiService.storico(id, limite));
    } catch {
      setStorico([]);
    }
  }, [limite]);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { if (x?.id) caricaStorico(x.id); }, [x?.id, caricaStorico]);

  // Più spesso mentre qualcosa è in esecuzione, per vedere l'avanzamento.
  const qualcosaInCorso = aggiornamenti.some((a) => a.stato === 'in_corso');
  useEffect(() => {
    const t = setInterval(() => {
      carica();
      if (x?.id) caricaStorico(x.id);
    }, qualcosaInCorso ? 3000 : 10000);
    return () => clearInterval(t);
  }, [qualcosaInCorso, carica, caricaStorico, x?.id]);

  const scegli = (id: string) => {
    setModificaFrequenza(false);
    setLimite(20);
    setParams((p) => { const n = new URLSearchParams(p); n.set('dato', id); return n; }, { replace: true });
  };

  const salvaFrequenza = async (f: Frequenza) => {
    if (!x) return;
    try {
      await aggiornamentiService.modifica(x.id, { frequenza: f });
      toast.success('Frequenza salvata');
      setModificaFrequenza(false);
      carica();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Frequenza non salvata');
    }
  };

  const cambiaAttivo = async (attivo: boolean) => {
    if (!x) return;
    try {
      await aggiornamentiService.modifica(x.id, { attivo });
      carica();
    } catch {
      toast.error('Modifica non salvata');
    }
  };

  const eseguiOra = async () => {
    if (!x) return;
    setAzione(true);
    try {
      const r = await aggiornamentiService.esegui(x.id);
      if (r.status === 'triggered') toast.success(`${x.nome}: esecuzione avviata`);
      else if (r.status === 'riavvio') toast.success(`${x.nome}: esecuzione interrotta, riparte subito`);
      else if (r.status === 'dopo_completa') toast.info(`${x.nome}: aggiornamento previsto al termine della lettura completa`);
      else if (r.status === 'in_attesa_turno') toast.info(`${x.nome}: in coda, limite di 2 esecuzioni contemporanee`);
      else toast.info(`${x.nome}: già in esecuzione`);
      carica();
      caricaStorico(x.id);
    } catch {
      toast.error('Esecuzione non avviata');
    } finally {
      setAzione(false);
    }
  };

  const interrompi = async () => {
    if (!x) return;
    setAzione(true);
    try {
      const r = await aggiornamentiService.interrompi(x.id);
      toast.info(r.status === 'stopping' ? `${x.nome}: interruzione richiesta` : `${x.nome}: non è in esecuzione`);
      carica();
    } finally {
      setAzione(false);
    }
  };

  if (!dati) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 rounded-full border-2 border-[#2B5EA7] border-t-transparent animate-spin" />
      </div>
    );
  }

  const indicatori = [
    { label: 'Aggiornamenti attivi', val: dati.indicatori.attivi, color: 'text-[#2B5EA7]' },
    { label: 'In esecuzione', val: dati.indicatori.in_esecuzione, color: 'text-blue-600' },
    { label: 'In coda', val: dati.indicatori.in_coda, color: 'text-amber-600' },
    { label: 'Errori (ultime 24 ore)', val: dati.indicatori.errori_24h, color: 'text-red-600' },
    { label: 'Aggiornamenti diretti oggi', val: dati.indicatori.diretti_oggi, color: 'text-teal-600' },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="admin-aggiornamenti">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 text-[#2B5EA7]" />
            Aggiornamenti dal gestionale
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            Lettura dei dati da NTS Business: aggiornamenti pianificati e diretti
          </p>
        </div>
        <button onClick={() => { carica(); if (x) caricaStorico(x.id); }} title="Aggiorna la pagina"
          className="h-9 w-9 rounded-xl border bg-white flex items-center justify-center hover:bg-gray-50 transition">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        {indicatori.map((k) => (
          <div key={k.label} className="bg-white rounded-xl border px-4 py-3">
            <div className={`text-2xl font-bold ${k.color}`}>{k.val}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      {dati.avvisi.length > 0 && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-1"
          data-testid="aggiornamenti-avvisi">
          {dati.avvisi.map((a) => (
            <button key={a.id} onClick={() => scegli(a.id)} className="flex items-start gap-2 text-left">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span><span className="font-semibold">{a.nome}</span> — {a.errore ?? 'Ultima esecuzione in errore'}</span>
            </button>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <nav className="min-w-0 rounded-xl border bg-white overflow-hidden self-start">
          {dati.gruppi.filter((g) => aggiornamenti.some((a) => a.gruppo === g)).map((gruppo) => (
            <Fragment key={gruppo}>
              <div className="px-4 py-2 bg-gray-50 border-b text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                {gruppo}
              </div>
              {aggiornamenti.filter((a) => a.gruppo === gruppo).map((a) => {
                const pallino = a.stato !== 'inattivo' ? STATO[a.stato].dot
                  : a.ultima ? (ESITO[a.ultima.esito] ?? ESITO.error).dot : STATO.inattivo.dot;
                return (
                  <button key={a.id} onClick={() => scegli(a.id)} data-testid={`aggiornamento-${a.id}`}
                    className={`flex w-full items-center justify-between gap-2 px-4 py-3 border-b last:border-b-0 text-left transition-colors ${x?.id === a.id ? 'bg-blue-50/60' : 'hover:bg-gray-50/60'}`}>
                    <span className="min-w-0">
                      <span className={`block text-sm font-medium ${a.attivo ? '' : 'text-muted-foreground'}`}>{a.nome}</span>
                      <span className="block text-[11px] text-muted-foreground truncate">
                        {a.attivo ? `Prossima esecuzione ${quando(a.prossima_esecuzione)}` : 'Disattivato'}
                      </span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className={`w-2 h-2 rounded-full ${pallino}`} title={STATO[a.stato].label} />
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </span>
                  </button>
                );
              })}
            </Fragment>
          ))}
        </nav>

        {x && (
          <div className="min-w-0 space-y-4">
            <div className="rounded-xl border bg-white px-5 py-4 flex flex-wrap items-center gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold">{x.nome}</h2>
                <p className="text-xs text-muted-foreground font-mono">{x.origine}</p>
              </div>
              <div className="ml-auto"><Badge cls={STATO[x.stato].badge}>{STATO[x.stato].label}</Badge></div>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <Card icon={CalendarClock} title="Aggiornamento pianificato" right={
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  Attivo
                  <input type="checkbox" className="accent-[#2B5EA7]" checked={x.attivo}
                    onChange={(e) => cambiaAttivo(e.target.checked)} data-testid="aggiornamento-attivo" />
                </label>
              }>
                <div className="flex-1 flex flex-col gap-5">
                  {modificaFrequenza ? (
                    <FrequenzaEditor valore={x.frequenza} onAnnulla={() => setModificaFrequenza(false)} onSalva={salvaFrequenza} />
                  ) : (
                    <dl className="grid grid-cols-[170px_1fr] gap-x-4 gap-y-3 text-sm">
                      <dt className="text-muted-foreground">Frequenza</dt>
                      <dd className="flex items-start gap-1.5">
                        {testoFrequenza(x)}
                        <button title="Modifica frequenza" onClick={() => setModificaFrequenza(true)}
                          className="mt-0.5 text-muted-foreground hover:text-foreground">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </dd>
                      <dt className="text-muted-foreground">Prossima esecuzione</dt>
                      <dd>{x.attivo ? `${quando(x.prossima_esecuzione)}${x.prossima_completa ? ' · lettura completa' : ''}` : '—'}</dd>
                      <dt className="text-muted-foreground">Ultima esecuzione</dt>
                      <dd>
                        {x.ultima ? (
                          <span className="flex flex-wrap items-center gap-2">
                            <EsitoBadge esito={x.ultima.esito} />
                            <span className="text-xs text-muted-foreground">
                              {quando(x.ultima.at)}
                              {x.ultima.righe != null && ` · ${x.ultima.righe.toLocaleString('it-IT')} righe`}
                              {x.ultima.durata != null && ` · ${Math.round(x.ultima.durata)} s`}
                            </span>
                          </span>
                        ) : '—'}
                      </dd>
                    </dl>
                  )}
                  {x.ultima?.esito === 'error' && x.ultima.errore && (
                    <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{x.ultima.errore}</p>
                  )}
                  {(x.stato === 'in_corso' || x.stato === 'in_coda') && x.avanzamento && x.avanzamento.total > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{x.avanzamento.current_item ?? `${x.avanzamento.current} di ${x.avanzamento.total}`}</span>
                        <span>{Math.round((100 * x.avanzamento.current) / x.avanzamento.total)}%</span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-secondary overflow-hidden">
                        <div className="h-2 rounded-full bg-[#2B5EA7]"
                          style={{ width: `${Math.min(100, (100 * x.avanzamento.current) / x.avanzamento.total)}%` }} />
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    {x.stato === 'in_corso' && !x.lettura_completa_in_corso && (
                      <button onClick={interrompi} disabled={azione}
                        className="h-9 px-4 rounded-lg border text-sm text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-50">
                        <StopCircle className="w-3.5 h-3.5" />Interrompi
                      </button>
                    )}
                    {x.stato === 'in_coda' ? (
                      <button disabled className="h-9 px-5 rounded-lg border text-sm text-muted-foreground inline-flex items-center gap-1.5">
                        <CalendarClock className="w-3.5 h-3.5" />Aggiornamento previsto al termine
                      </button>
                    ) : x.stato === 'in_corso' && x.lettura_completa_in_corso ? (
                      <button onClick={eseguiOra} disabled={azione} data-testid="aggiornamento-esegui"
                        className="h-9 px-5 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 hover:bg-gray-50 disabled:opacity-50">
                        <CalendarClock className="w-3.5 h-3.5" />Aggiorna al termine della lettura completa
                      </button>
                    ) : (
                      <button onClick={eseguiOra} disabled={azione} data-testid="aggiornamento-esegui"
                        className="h-9 px-5 rounded-lg bg-[#2B5EA7] text-white text-sm font-medium hover:bg-[#1E4D8C] transition inline-flex items-center gap-1.5 disabled:opacity-50">
                        {x.stato === 'in_corso'
                          ? <><RefreshCw className="w-3.5 h-3.5" />Interrompi e riavvia</>
                          : <><Play className="w-3.5 h-3.5" />Esegui ora</>}
                      </button>
                    )}
                  </div>
                  <Nota>
                    {x.lettura_completa_in_corso
                      ? 'La lettura completa non si interrompe: rileva i dati rimossi dal gestionale. Al termine viene eseguito un aggiornamento in delta.'
                      : x.stato === 'in_corso'
                        ? "Un avvio manuale durante un'esecuzione in corso la interrompe e la riavvia."
                        : 'Al massimo due aggiornamenti pianificati sono in esecuzione insieme: un avvio manuale senza posto libero parte appena se ne libera uno.'}
                  </Nota>
                </div>
              </Card>

              <Card icon={Zap} title={x.diretto ? DIRETTI[x.diretto]?.titolo ?? 'Aggiornamento diretto' : 'Aggiornamento diretto'}>
                <div className="flex-1 flex flex-col gap-5">
                  {x.diretto === 'ordine' ? (
                    <DirettoOrdine onFatto={() => { carica(); caricaStorico(x.id); }} />
                  ) : x.diretto === 'articolo' ? (
                    <DirettoArticoli onFatto={() => { carica(); caricaStorico(x.id); }} />
                  ) : x.diretto === 'conto' ? (
                    <DirettoConto onFatto={() => { carica(); caricaStorico(x.id); }} />
                  ) : (
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Per aggiornare subito questo dato usa «Esegui ora».
                    </p>
                  )}
                  <Nota>Se il gestionale non restituisce un dato non viene modificato nulla: la rimozione è rilevata solo dalla lettura completa.</Nota>
                </div>
              </Card>
            </div>

            <div className="rounded-xl border bg-white overflow-hidden">
              <div className="px-5 py-4 border-b flex items-center gap-3">
                <History className="w-4 h-4 text-muted-foreground" />
                <h2 className="font-semibold">Storico esecuzioni — {x.nome}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="aggiornamento-storico">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className={TH}>Ora</th><th className={TH}>Tipo</th><th className={TH}>Avvio</th>
                      <th className={TH}>Chiavi</th><th className={TH}>Esito</th>
                      <th className={`${TH} text-right`}>Durata</th><th className={`${TH} text-right`}>Righe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storico.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-6 text-sm text-center text-muted-foreground">Nessuna esecuzione registrata.</td></tr>
                    )}
                    {storico.map((e) => (
                      <tr key={`${e.at}-${e.tipo}`} className="border-b last:border-b-0 hover:bg-gray-50/60 align-top">
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{quando(e.at)}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {e.tipo === 'diretto' ? 'Diretto' : e.completa ? 'Lettura completa' : 'Pianificato'}
                        </td>
                        <td className="px-4 py-3 text-sm">{testoAvvio(e.avviato_da)}</td>
                        <td className="px-4 py-3 text-sm font-mono text-[#2B5EA7] whitespace-nowrap">{e.chiavi.length ? e.chiavi.join(', ') : '—'}</td>
                        <td className="px-4 py-3">
                          <EsitoBadge esito={e.esito} />
                          {e.errore && <div className="text-[11px] text-muted-foreground mt-1 max-w-sm">{e.errore}</div>}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-muted-foreground whitespace-nowrap">
                          {e.durata != null ? `${Math.round(e.durata)} s` : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-muted-foreground whitespace-nowrap">
                          {e.righe != null ? e.righe.toLocaleString('it-IT') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {storico.length >= limite && limite < 500 && (
                <div className="px-5 py-3 border-t flex justify-center">
                  <button onClick={() => setLimite((l) => Math.min(500, l + 50))}
                    className="h-9 px-4 rounded-lg border text-sm text-muted-foreground hover:text-foreground hover:bg-gray-50">
                    Mostra altre esecuzioni
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
